import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { PageLoader, useSnackbars } from '@customerio/pluma-components/react';
import { sessionsStore } from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { AuthFormLayout } from './components/auth-form-layout';
import { LoginFlowProvider, useLoginFlow, type LoginStep } from './login-flow';
import { resolveEffectiveStep } from './utils/effective-step';
import { EmailStep } from './steps/email-step';
import { PasswordStep } from './steps/password-step';
import { TwoFactorStep } from './steps/two-factor-step';
import { TwoFactorEmailLinkStep } from './steps/two-factor-email-link-step';
import { SsoStep } from './steps/sso-step';
import { ChooseUserStep } from './steps/choose-user-step';

type LoginPageProps = {
  step: LoginStep;
  onNavigateStep: (step: LoginStep) => void;
  initialEmail: string;
  isAddingAccount: boolean;
  origin: string | undefined;
  emailLinkToken: string | undefined;
  showLoggedOut: boolean;
  /** Called once after the "Logged out" toast fires, so the route can strip
   *  `?loggedOut` from the URL and a refresh doesn't re-show it. */
  onLoggedOutShown?: () => void;
};

type LoginStepViewProps = {
  effectiveStep: ReturnType<typeof resolveEffectiveStep>;
  emailLinkToken: string | undefined;
};

function LoginStepView({ effectiveStep, emailLinkToken }: LoginStepViewProps) {
  switch (effectiveStep) {
    case 'email':
      return <EmailStep />;
    case 'password':
      return <PasswordStep />;
    case 'two_factor_authentication':
      return <TwoFactorStep />;
    case '2fa_email_link':
      return <TwoFactorEmailLinkStep token={emailLinkToken} />;
    case 'sso':
      return <SsoStep />;
    case 'choose_user':
      return <ChooseUserStep />;
    default: {
      const _exhaustive: never = effectiveStep;
      return _exhaustive;
    }
  }
}

type LoginContentProps = {
  emailLinkToken: string | undefined;
};

/**
 * Lives inside the flow provider so the layout and the step view agree on the
 * *effective* step. When a stale `/login/sso` (etc.) falls back to the email
 * step, the referral splash must come back too — keying it off the URL step
 * would show the centered email form without the splash `/login` renders.
 */
function LoginContent({ emailLinkToken }: LoginContentProps) {
  const { step, email, ssoConfiguration, chooseUserEntries, hasSubmittedEmail, twoFactorAuthLoginToken } =
    useLoginFlow();

  const effectiveStep = resolveEffectiveStep(step, {
    email,
    ssoConfiguration,
    chooseUserEntries,
    hasSubmittedEmail,
    twoFactorAuthLoginToken,
  });

  return (
    <AuthFormLayout withReferralSplash={effectiveStep === 'email'}>
      <LoginStepView effectiveStep={effectiveStep} emailLinkToken={emailLinkToken} />
    </AuthFormLayout>
  );
}

export default function LoginPage({
  step,
  onNavigateStep,
  initialEmail,
  isAddingAccount,
  origin,
  emailLinkToken,
  showLoggedOut,
  onLoggedOutShown,
}: LoginPageProps) {
  const snackbars = useSnackbars();
  const isAuthenticated = useStore(session.isAuthenticated);
  const isInitialized = useStore(session.isInitialized);
  const hasPersistedSession = useStore(sessionsStore).length > 0;

  useEffect(() => {
    if (showLoggedOut) {
      snackbars.create({ variant: 'default', id: 'loggedOut.success', message: 'Logged out' });
      // Drop `?loggedOut` so a refresh doesn't re-fire the toast (Ember nulled
      // the query param after showing it).
      onLoggedOutShown?.();
    }
  }, [showLoggedOut, snackbars, onLoggedOutShown]);

  // The redirect below only applies to authenticated users on the normal login
  // steps. Skip it for the add-account flow (you're signed in while adding
  // another account) and the 2FA email-link step (re-auth completes while
  // technically still authenticated).
  const redirectApplies = !isAddingAccount && step !== '2fa_email_link';

  // While a persisted session exists but the `/accounts/current` bootstrap
  // hasn't resolved, `isAuthenticated` is still `false` — we don't yet know
  // whether to redirect. Show a loader instead of flashing the login form (which
  // would then vanish once auth resolves), mirroring Ember's route loader during
  // session restore. A visitor with no persisted session is definitely logged
  // out, so the form renders immediately with no delay.
  if (redirectApplies && hasPersistedSession && !isInitialized) {
    return <PageLoader />;
  }

  // Already-authenticated users don't belong on the login page — send them to
  // the app (Ember's `prohibitAuthenticated`).
  if (redirectApplies && isAuthenticated) {
    return <SafeNavigate to="/" options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <LoginFlowProvider
      step={step}
      onNavigateStep={onNavigateStep}
      initialEmail={initialEmail}
      isAddingAccount={isAddingAccount}
      origin={origin}
    >
      <LoginContent emailLinkToken={emailLinkToken} />
    </LoginFlowProvider>
  );
}
